import React from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import {
    AI_WORKFLOW_STEP_COUNT,
    type AiWorkflowStepKey,
} from '../utils/aiWorkflowSteps';
import SparklesIcon from './icons/SparklesIcon';

interface RecipeAiProgressOverlayProps {
    stepKey: AiWorkflowStepKey;
    language?: Language;
    stepIndex: number;
    zIndexClass?: string;
}

const RecipeAiProgressOverlay: React.FC<RecipeAiProgressOverlayProps> = ({
    stepKey,
    language,
    stepIndex,
    zIndexClass = 'z-[300]',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const isSavingStep = stepIndex >= AI_WORKFLOW_STEP_COUNT - 1;
    const spinAccent = isSavingStep ? 'border-t-brand-sage' : 'border-t-brand-orange';
    const barAccent = isSavingStep ? 'bg-brand-sage' : 'bg-brand-orange';
    const iconAccent = isSavingStep ? 'text-brand-sage' : 'text-brand-orange';
    const dotAccent = isSavingStep ? 'bg-brand-sage' : 'bg-brand-orange';

    return (
        <div className={`absolute inset-0 ${zIndexClass} bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex flex-col items-center justify-center animate-fade-in`}>
            <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-6 border border-slate-100 dark:border-slate-700 max-w-sm w-[calc(100%-2rem)]">
                <div className="relative h-20 w-20">
                    <div className={`absolute inset-0 border-4 border-slate-100 dark:border-slate-700 ${spinAccent} rounded-full animate-spin`} />
                    <div
                        className={`absolute inset-2 border-[3px] border-transparent ${isSavingStep ? 'border-b-brand-sage/70' : 'border-b-brand-orange/70'} rounded-full animate-spin`}
                        style={{ animationDirection: 'reverse', animationDuration: '1.1s' }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center animate-ai-sparkle-pulse">
                        <SparklesIcon className={`w-8 h-8 ${iconAccent}`} />
                    </div>
                </div>

                <div className="text-center w-full space-y-4">
                    <div>
                        <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 leading-snug">
                            {t(stepKey)}
                        </h3>
                        <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
                            {[0, 1, 2].map((dotIndex) => (
                                <span
                                    key={dotIndex}
                                    className={`h-1.5 w-1.5 rounded-full ${dotAccent} animate-ai-bounce-dot`}
                                    style={{ animationDelay: `${dotIndex * 0.18}s` }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className={`h-full w-1/3 rounded-full ${barAccent} animate-ai-progress-slide`} />
                    </div>

                    <p dir="ltr" className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tabular-nums">
                        {stepIndex + 1} / {AI_WORKFLOW_STEP_COUNT}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RecipeAiProgressOverlay;
